import { Dropdown, Table } from 'antd';
import type { MenuProps, TableProps } from 'antd';
import type { ColumnType, ColumnsType } from 'antd/es/table';
import { ChevronRight, MoreHorizontal } from 'lucide-react';
import { useCallback, useMemo, useRef, useState } from 'react';
import type { CSSProperties, KeyboardEvent, ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import type { UseTreeExpansion } from '@/hooks/useTreeExpansion';
import { cn } from '@/lib/utils';

/**
 * ─────────────────────────────────────────────────────────────────────────────────────
 *  A TABLE WHOSE FIRST COLUMN IS A TREE, and whose remaining columns carry real data.
 *
 *  Not a file explorer with a details panel bolted on: the data columns are ordinary
 *  columns, aligned across every depth, because the counts beside a unit are the point.
 *
 *  ─── Built on AntD's `Table` + `expandable`, deliberately ──────────────────────────
 *  A hand-rolled <table> would have to re-implement column definition, widths, fixed
 *  columns, `rowSelection` with its select-all and indeterminate header checkbox, the
 *  loading overlay and the empty state — several hundred lines of accessible-table
 *  plumbing, with its own bug surface, to arrive at markup that renders the same. It
 *  would also become a FOURTH styling surface in a repo that spent real effort getting
 *  three to coexist. The three things AntD does not give — indent guides, tree keyboard
 *  navigation, treegrid ARIA — are all additive through `onCell` and `onRow`.
 *
 *  ─── Depth is unbounded ────────────────────────────────────────────────────────────
 *  Nothing here or in `hierarchyTable.css` encodes a level. Rows carry their depth
 *  RELATIVE TO THE RENDERED FOREST ROOT (see `OrgTreeRow.treeDepth`) — never the server's
 *  `depth`, which would indent a scoped nodal's own root by two levels.
 *
 *  ─── Neither lazy-loaded nor virtualised, and that is the answer, not an omission ───
 *  The tree arrives in ONE request, so deferring children would mean more requests, not
 *  fewer. A collapsed subtree already contributes zero DOM, and the default expansion
 *  renders a handful of rows. AntD's `virtual` needs a fixed pixel `scroll.y` and measured
 *  row heights, and jsdom reports 0 for every height — it would make the screen untestable
 *  to buy performance for a case that does not exist. What IS lazy is the counts: the
 *  consumer fires those per rendered row.
 * ─────────────────────────────────────────────────────────────────────────────────────
 */

/** The ONE indent number. `hierarchyTable.css` reads the same value as a custom property. */
export const TREE_INDENT_PX = 20;

export type HierarchyRow = {
  key: string;
  treeDepth: number;
  parentKey?: string;
  isLastChild: boolean;
  children?: HierarchyRow[];
};

export type HierarchyTableProps<T extends HierarchyRow> = {
  rows: T[];
  /** The tree column. `code` renders the monospace chip, `name` the label. */
  tree: {
    title: ReactNode;
    width?: number | string;
    /** ⚠️ `null` renders NO chip — see the render below. An empty chip is furniture. */
    code: (row: T) => ReactNode;
    name: (row: T) => ReactNode;
    /**
     * ⚠️ [C3c.1] RENDERED OUTSIDE THE TRUNCATING BOX — see the render below. For anything that
     * states a FACT ABOUT THE ROW rather than naming it, and must survive a long name.
     */
    badge?: (row: T) => ReactNode;
  };
  /** Ordinary data columns. Every one MUST carry a stable `key`. */
  columns: ColumnsType<T>;
  /**
   * The trailing overflow menu. Returns MENU ITEMS rather than nodes on purpose — that is
   * what stops the column decaying back into a row of unlabelled icons one prop at a time.
   */
  rowActions?: (row: T) => MenuProps['items'];
  expansion: UseTreeExpansion;
  selectedKey?: string | undefined;
  onSelect?: (row: T) => void;
  /** Enter, Space, and double-click. */
  onOpen?: (row: T) => void;
  selection?: {
    selectedKeys: string[];
    onChange: (keys: string[]) => void;
    /** Default true: selecting a parent does not select its children. */
    checkStrictly?: boolean;
  };
  loading?: boolean;
  /**
   * ⚠️ [C3c §2f] SERVER PAGINATION, PASSED THROUGH — omitted means `false`, exactly as before.
   *
   * This table has never paginated, because its one consumer fetched a whole tree. §2 gives it a
   * SERVER-paginated one, and `useServerTable` reports both page changes and SORTER changes
   * through AntD's single `onChange` — so rendering a pager beside the table solves only half:
   * the sortable columns would have nowhere to send a sort.
   *
   * ⚠️ THE PAGER LANDS OUTSIDE `role="treegrid"`, WHICH IS CORRECT AND IS WORTH KNOWING RATHER
   * THAN ASSUMING. AntD renders `<ul class="ant-pagination">` as a SIBLING of `.ant-table`, and
   * the `components.table` override below replaces only the `<table>` element — so no control
   * ends up inside the grid.
   */
  pagination?: TableProps<T>['pagination'];
  onChange?: TableProps<T>['onChange'];
  empty?: ReactNode;
  ariaLabel: string;
  'data-testid'?: string;
};

/** Depth-first over what is currently visible — the order the arrow keys move through. */
const flattenVisible = <T extends HierarchyRow>(rows: readonly T[], expanded: ReadonlySet<string>): T[] =>
  rows.flatMap((row) =>
    row.children && expanded.has(row.key) ? [row, ...flattenVisible(row.children as T[], expanded)] : [row],
  );

export const HierarchyTable = <T extends HierarchyRow>({
  rows,
  tree,
  columns,
  rowActions,
  expansion,
  selectedKey,
  onSelect,
  onOpen,
  selection,
  loading,
  pagination,
  onChange,
  empty,
  ariaLabel,
  'data-testid': testId,
}: HierarchyTableProps<T>) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [focusedKey, setFocusedKey] = useState<string | undefined>(() => rows[0]?.key);

  const visible = useMemo(() => flattenVisible(rows, expansion.expandedSet), [rows, expansion.expandedSet]);

  // The focused row can disappear (a collapse, a filter). Re-anchor during render rather
  // than leaving `tabIndex={-1}` on every row, which would drop the table out of the tab
  // order entirely.
  const activeKey = visible.some((row) => row.key === focusedKey) ? focusedKey : visible[0]?.key;

  const move = useCallback((key: string | undefined) => {
    if (!key) return;
    setFocusedKey(key);
    const container = containerRef.current;
    // Only steal focus if focus is ALREADY inside the table. Otherwise the table would
    // grab it on mount and again on every refetch.
    if (!container?.contains(document.activeElement)) return;
    const node = container.querySelector<HTMLElement>(`tr[data-row-key="${CSS.escape(key)}"]`);
    node?.focus();
    // jsdom implements no layout and has NO Element#scrollIntoView. The optional call is
    // required, not defensive style.
    node?.scrollIntoView?.({ block: 'nearest' });
  }, []);

  const onKeyDown = useCallback(
    (event: KeyboardEvent<HTMLElement>, row: T) => {
      const index = visible.findIndex((candidate) => candidate.key === row.key);
      const hasChildren = (row.children?.length ?? 0) > 0;
      const isExpanded = expansion.expandedSet.has(row.key);

      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          move(visible[index + 1]?.key ?? row.key);
          break;
        case 'ArrowUp':
          event.preventDefault();
          move(visible[index - 1]?.key ?? row.key);
          break;
        case 'ArrowRight':
          event.preventDefault();
          if (hasChildren && !isExpanded) expansion.onExpand(true, row.key);
          else if (hasChildren) move(row.children?.[0]?.key);
          break;
        case 'ArrowLeft':
          event.preventDefault();
          if (hasChildren && isExpanded) expansion.onExpand(false, row.key);
          else move(row.parentKey);
          break;
        case 'Home':
          event.preventDefault();
          move(visible[0]?.key);
          break;
        case 'End':
          event.preventDefault();
          move(visible.at(-1)?.key);
          break;
        case 'Enter':
        case ' ':
          event.preventDefault();
          onOpen?.(row);
          break;
        default:
          break;
      }
    },
    [visible, expansion, move, onOpen],
  );

  /**
   * ⚠️ MEMOISED, and it is not an optimisation.
   *
   * `components.table` declared inline is a NEW component type on every render, so React
   * unmounts and remounts the entire table subtree each time — which silently destroys
   * keyboard focus mid-navigation and makes the treegrid unusable. Stable identity is a
   * correctness requirement here.
   */
  const components = useMemo(
    () => ({
      table: (props: Record<string, unknown>) => <table {...props} role="treegrid" aria-label={ariaLabel} />,
    }),
    [ariaLabel],
  );

  const treeColumn: ColumnType<T> = {
    title: tree.title,
    key: '__tree__',
    ...(tree.width === undefined ? {} : { width: tree.width }),
    onCell: (row) => ({
      className: 'pams-tree-cell',
      style: {
        '--pams-tree-depth': row.treeDepth,
        '--pams-tree-last': row.isLastChild ? 1 : 0,
        paddingInlineStart: 'var(--pams-tree-gutter)',
      } as CSSProperties,
    }),
    render: (_value, row) => {
      /*
       * ⚠️ [C3c.2] A CODE THAT IS NOT THERE RENDERS NO CHIP, RATHER THAN AN EMPTY ONE.
       *
       * The chip is a fixed-padding box, so a `null` code still painted a bare grey lozenge
       * before the title — furniture that says nothing. `null` is now the way a consumer says
       * "this row has no code", and the Inbox uses it for an order with no PA number.
       */
      const code = tree.code(row);

      return (
        /*
         * ⚠️ [C3c.2] `flex`, NOT `inline-flex`, AND THAT ONE WORD IS WHAT KEEPS THE ROW ON ONE
         * LINE. The chevron beside this box is `float-left`, and CSS 2.1 §9.5 treats the two
         * cases completely differently:
         *
         *   • an INLINE-level box that establishes a formatting context sits on the line box,
         *     and when its shrink-to-fit width does not fit in the space the float leaves, it
         *     is pushed to the NEXT LINE — the chevron alone on line one, the chip and the
         *     title wrapped underneath it, which is what the Inbox was rendering;
         *   • a BLOCK-level one "must not overlap the margin box of any float", which browsers
         *     satisfy by NARROWING it into the space beside the float. It cannot drop.
         *
         * This is the ordinary media-object layout, and it is why the inner `truncate` is what
         * absorbs a long title now instead of the whole box moving. Nothing about the chevron,
         * the indent spans or `hierarchyTable.css`'s rails changes.
         */
        <span className="pams-tree-content flex min-w-0 items-center gap-2">
          {code === null || code === undefined ? null : (
            <span className="shrink-0 whitespace-nowrap rounded bg-secondary px-1.5 py-0.5 font-mono text-xs text-muted-foreground">
              {code}
            </span>
          )}
          <span className="min-w-0 truncate">{tree.name(row)}</span>
          {/*
          ⚠️ [amended C3c.1] A SLOT **OUTSIDE** THE TRUNCATING SPAN, FOR ANYTHING THAT MUST NOT BE
          CLIPPED.

          The name lives in a `truncate` box — `overflow:hidden; text-overflow:ellipsis;
          white-space:nowrap` — inside a fixed-width column. Anything rendered AFTER the name
          inside that box is the first thing an ellipsis eats, so a badge appended there is
          invisible for every row whose title is of ordinary length. The Inbox's "40 of 192 duties
          shown" notice was exactly that, and it is the one thing on that row an operator must not
          miss: it says work is absent from the list.

          ⚠️ `shrink-0` ON THE SLOT AND `min-w-0` ON THE NAME, so the NAME gives way and the badge
          does not — which is the whole point of separating them.
        */}
          {tree.badge === undefined ? null : <span className="shrink-0">{tree.badge(row)}</span>}
        </span>
      );
    },
  };

  const actionsColumn: ColumnType<T> | undefined = rowActions && {
    key: '__actions__',
    title: '',
    width: 56,
    align: 'right',
    render: (_value, row) => (
      /*
       * ⚠️ [P4-UX] THE STOP IS ON THE WRAPPER, NOT ONLY ON THE BUTTON, AND A PORTAL IS WHY.
       *
       * AntD renders the menu into `document.body`, but React's synthetic events propagate
       * through the REACT tree rather than the DOM tree — so a click on "Move…" bubbles out
       * of the portal, through this cell, and into the row's `onClick`. Selecting a row and
       * opening its action at the same time was invisible while the detail panel rendered
       * quietly below the table; now that it is an overlay, choosing "Move…" would put the
       * move modal on top of a detail drawer nobody asked for.
       *
       * `OrgTree.test.tsx`'s "shows the blast radius before confirming a move" is what
       * caught it — it asserts a single `role="dialog"` — and it is unchanged.
       */
      <span onClick={(event) => event.stopPropagation()}>
        <Dropdown menu={{ items: rowActions(row) }} trigger={['click']} placement="bottomRight">
          <Button variant="ghost" size="icon" className="size-8" aria-label="Row actions">
            <MoreHorizontal aria-hidden />
          </Button>
        </Dropdown>
      </span>
    ),
  };

  const allColumns: ColumnsType<T> = [treeColumn, ...columns, ...(actionsColumn ? [actionsColumn] : [])];

  const onRow: TableProps<T>['onRow'] = (row) => ({
    tabIndex: row.key === activeKey ? 0 : -1,
    // Annotated: rc-table types the handler as `KeyboardEventHandler<any>`, and an
    // implicit `any` fails `no-unsafe-argument`.
    onKeyDown: (event: KeyboardEvent<HTMLElement>) => onKeyDown(event, row),
    onFocus: () => setFocusedKey(row.key),
    onClick: () => onSelect?.(row),
    onDoubleClick: () => onOpen?.(row),
    'aria-level': row.treeDepth + 1,
    'aria-expanded': (row.children?.length ?? 0) > 0 ? expansion.expandedSet.has(row.key) : undefined,
    'aria-selected': row.key === selectedKey,
    // Read by the CSS that suppresses the elbow on a forest root.
    'data-tree-depth': row.treeDepth,
  });

  return (
    <div ref={containerRef} data-testid={testId} className="pams-hierarchy">
      <Table<T>
        rowKey="key"
        dataSource={rows}
        columns={allColumns}
        pagination={pagination ?? false}
        loading={loading ?? false}
        size="middle"
        /*
         * ⚠️ [P4-UX] THE SCROLL BELONGS TO ANTD, NOT TO THE WRAPPER.
         *
         * This used to be `overflow-x-auto` on the div above, which scrolls correctly and
         * is invisible to rc-table — so the table never got `ant-table-ping-right` and the
         * edge shadow that says "there is more to the right" was never drawn at all. Moving
         * the scroll onto AntD's own container is what makes the affordance appear.
         * `max-content` rather than `true`: `x: true` emits an invalid `width: true` and
         * leaves `table-layout: auto`, so the browser COMPRESSES columns to fit instead of
         * overflowing, which is why the scrollbar was unreliable in the first place.
         */
        scroll={{ x: 'max-content' }}
        onChange={onChange}
        onRow={onRow}
        style={{ '--pams-tree-indent': `${TREE_INDENT_PX}px` } as CSSProperties}
        locale={empty ? { emptyText: empty } : {}}
        {...(selection
          ? {
              rowSelection: {
                selectedRowKeys: selection.selectedKeys,
                onChange: (keys) => selection.onChange(keys.map(String)),
                checkStrictly: selection.checkStrictly ?? true,
              },
            }
          : {})}
        expandable={{
          indentSize: TREE_INDENT_PX,
          expandedRowKeys: expansion.expandedKeys,
          onExpand: (expanded, row) => expansion.onExpand(expanded, row.key),
          /*
           * The chevron is NOT focusable. Focus lives on the row — one tab stop for the
           * whole table, arrows to move — which is the treegrid contract and what makes
           * ArrowRight/Left expected rather than surprising. A focusable chevron would add
           * a second tab stop per row that duplicates an affordance the row already has.
           *
           * `stopPropagation` matters: without it, clicking the chevron also fires the
           * row's onClick and opens the detail panel.
           */
          expandIcon: ({ expanded, expandable, onExpand, record }) =>
            expandable ? (
              <span
                aria-hidden
                className="pams-tree-chevron float-left mr-1 inline-flex size-5 cursor-pointer items-center justify-center rounded text-muted-foreground transition-colors hover:bg-hover hover:text-foreground"
                onClick={(event) => {
                  event.stopPropagation();
                  onExpand(record, event);
                }}
              >
                <ChevronRight className={cn('size-3.5 transition-transform', expanded && 'rotate-90')} />
              </span>
            ) : (
              // A leaf must occupy the same width, or names stop aligning across siblings.
              <span aria-hidden className="pams-tree-chevron float-left mr-1 inline-block size-5" />
            ),
        }}
        components={components}
      />
    </div>
  );
};
